import React, { useEffect, useRef, useState } from 'react';
import ExoForm from '../exo/ExoForm';
import { useTranslation } from 'react-i18next';
import { API_PATCH, API_POST, DBRequest, useConfig } from '../../api/api';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import EmailIcon from '@mui/icons-material/Email';
import EditNoteIcon from '@mui/icons-material/EditNote';
import Cookies from 'universal-cookie';
import * as Yup from 'yup';
import { TypeSmallIconCards } from '../form/form-fields/TypeSmallIconCards';
import { Box, Typography } from '@mui/material';
import moment from 'moment';
import InsertInvitationIcon from '@mui/icons-material/InsertInvitation';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

export const NewTask = ({
  targetType,
  targetId,
  onClose,
  startData,
  onChange,
  onCreate,
  onFeedback,
}) => {
  const { t } = useTranslation();
  const config = useConfig();
  const cookies = new Cookies();
  const userId = cookies.get('userId');

  const [startDataCollection, setStartDataCollection] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const ref = useRef();

  useEffect(() => {
    let newStartData = startData || {};

    // If editing an existing task with a combined datetime in 'deadline', split it
    if (newStartData.deadline) {
      newStartData = {
        ...newStartData,
        time: moment(newStartData.deadline),
      };
    }

    if (!newStartData.user) newStartData.user = { id: userId };
    if (!newStartData.type) newStartData.type = 'call';
    setStartDataCollection(newStartData);
  }, [startData]);

  const type = startDataCollection?.type || 'call';

  const fieldsMap = {
    call: [
      {
        label: t('Name'),
        key: 'name',
        type: 'text',
        colspan: 2,
      },
      {
        label: t('Date'),
        key: 'deadline',
        type: 'date',
        colspan: 1,
        Icon: InsertInvitationIcon,
      },
      {
        label: t('Time'),
        key: 'time',
        type: 'time',
        Icon: AccessTimeIcon,
        colspan: 1,
      },
      {
        label: t('Description'),
        key: 'description',
        type: 'richTextEditor',
        colspan: 2,
      },
      {
        label: t('Responsible Employee'),
        key: 'user',
        type: 'user',
        colspan: 2,
      },
      {
        label: t('Reminder'),
        key: 'reminders',
        type: 'reminder',
        colspan: 2,
        dateKey: 'deadline',
        timeKey: 'time',
      },
      {
        label: t('Partner'),
        key: 'target',
        type: targetType && targetId ? 'hidden' : 'autoCompleteClient',
        searchParams: ['company_leads', 'person_leads'],
        colspan: 2,
      },
    ],
    email: [
      {
        label: t('Name'),
        key: 'name',
        type: 'text',
        colspan: 2,
      },
      {
        label: t('Date'),
        key: 'deadline',
        type: 'date',
        colspan: 1,
        Icon: InsertInvitationIcon,
      },
      {
        label: t('Time'),
        key: 'time',
        type: 'time',
        Icon: AccessTimeIcon,
        colspan: 1,
      },
      {
        label: t('Description'),
        key: 'description',
        type: 'richTextEditor',
        colspan: 2,
      },
      {
        label: t('Responsible Employee'),
        key: 'user',
        type: 'user',
        colspan: 2,
      },
      {
        label: t('Reminder'),
        key: 'reminders',
        type: 'reminder',
        colspan: 2,
        dateKey: 'deadline',
        timeKey: 'time',
      },
      {
        label: t('Partner'),
        key: 'target',
        type: targetType && targetId ? 'hidden' : 'autoCompleteClient',
        searchParams: ['company_leads', 'person_leads'],
        colspan: 2,
      },
    ],
    other: [
      {
        label: t('Name'),
        key: 'name',
        type: 'text',
        colspan: 2,
      },
      {
        label: t('Deadline'),
        key: 'deadline',
        type: 'date',
        Icon: InsertInvitationIcon,
        colspan: 1,
      },
      {
        label: t('Time'),
        key: 'time',
        type: 'time',
        Icon: AccessTimeIcon,
        colspan: 1,
      },
      {
        label: t('Description'),
        key: 'description',
        type: 'richTextEditor',
        colspan: 2,
      },
      {
        label: t('Responsible Employee'),
        key: 'user',
        type: 'user',
        colspan: 2,
      },
      {
        label: t('Reminder'),
        key: 'reminders',
        type: 'reminder',
        colspan: 2,
        dateKey: 'deadline',
        timeKey: 'time',
      },
      {
        label: t('Partner'),
        key: 'target',
        type: targetType && targetId ? 'hidden' : 'autoCompleteClient',
        searchParams: ['company_leads', 'person_leads'],
        colspan: 2,
      },
    ],
  };

  const fields = fieldsMap[type];

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t('Is required')),
    type: Yup.string().required(t('Is required')),
    user: Yup.object()
      .shape({ id: Yup.string().required(t('Is required')) })
      .required(t('Is required')),
    /*reminders: Yup.array().of(
      Yup.object().shape({
        remindAt: Yup.string()
          .required(t('Reminder date/time is required'))
          .test(function (value) {
            if (!value) return true;

            const reminderTime = moment(value);
            const now = moment();
            return reminderTime.isSameOrAfter(now);
          }),
        id: Yup.number().optional(),
        type: Yup.string().optional(),
        value: Yup.string().optional(),
      })
    ),*/
  });

  function handleSubmit(data) {
    const isUpdate = Boolean(data.id);

    // Merge deadline + time into full datetime
    let mergedDeadline = data.deadline;
    if (data.deadline && data.time) {
      const date = moment(data.deadline).format('YYYY-MM-DD');
      const time = moment(data.time).format('HH:mm');
      const merged = moment(`${date} ${time}`, 'YYYY-MM-DD HH:mm');
      mergedDeadline = merged.toISOString();
    }

    const { time, ...restData } = data;
    const reqData =
      targetType && targetId
        ? {
            ...restData,
            deadline: mergedDeadline, // ✅ use merged datetime
            targetType,
            target: { id: targetId },
          }
        : {
            ...restData,
            deadline: mergedDeadline, // ✅ use merged datetime
          };

    DBRequest({
      config,
      path: isUpdate ? `user-tasks/${data.id}` : `user-tasks`,
      method: isUpdate ? API_PATCH(reqData) : API_POST(reqData),
      onResponse: (res) => handleResponse(res, isUpdate),
      onFeedback,
      onLoading: setIsLoading,
    });
  }

  function handleResponse(data, isUpdate) {
    // Split merged deadline back into date + time
    let updatedData = { ...data };

    if (updatedData.deadline) {
      const deadlineMoment = moment(updatedData.deadline);
      updatedData.deadline = deadlineMoment.format('YYYY-MM-DD'); // just the date part
      updatedData.time = deadlineMoment.format('HH:mm'); // just the time part
    }
    onClose();

    if (isUpdate) onChange(updatedData);
    else onCreate(updatedData);
  }

  return (
    <Box className="flex flex-col w-full gap-2">
      <Typography variant="h3" textAlign="center" className="py-2">
        {startData && startData.id ? t('Update Task') : t('Create Task')}
      </Typography>
      <TypeSmallIconCards
        dataCollection={startDataCollection}
        updateValidationOnChange={(key, value) =>
          setStartDataCollection({
            ...ref.current.dataCollection,
            [key]: value,
          })
        }
        field={{
          label: t('What') + '?',
          key: 'type',
          type: 'smallIconCards',
          cards: [
            {
              label: t('Call'),
              Icon: LocalPhoneIcon,
              value: 'call',
            },
            {
              label: t('E-Mail'),
              Icon: EmailIcon,
              value: 'email',
            },
            {
              label: t('Other'),
              Icon: EditNoteIcon,
              value: 'other',
            },
          ],
        }}
      />
      <ExoForm
        ref={ref}
        fields={fields}
        onSubmit={handleSubmit}
        isLoading={isLoading}
        onCancel={onClose}
        startDataCollection={startDataCollection}
        noDifferences
        validationSchema={validationSchema}
        className="w-full grid grid-cols-2 gap-4 mt-2"
      />
    </Box>
  );
};
